import type { FC } from "react"
import React from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue, useSetRecoilState } from "recoil"
import styled from "styled-components"

import { preferredImageState } from "~/containers/LegacyDesigner/state/imageElements"
import { resetLocationModalIsOpenState } from "~/containers/LegacyDesigner/state/view"
import { editingState } from "~/containers/Proposal/state/activity"
import { useDesignActions } from "~/containers/Proposal/state/designs/design-context"
import { useProposalActions } from "~/containers/Proposal/state/proposal/proposal-context"
import {
  useSiteActions,
  useSiteStatus,
} from "~/containers/Proposal/state/site/site-context"
import { shouldRetainZoomState } from "~/containers/Proposal/state/site/site-state"

import { GoogleMap } from "./GoogleMap"
import { GeocodeMap } from "./Nearmap"
import Button from "../Button"
import FormFooter from "../FormFooter"

const Wrapper = styled.div`
  pointer-events: all;
  z-index: 20000;
  height: auto;
  width: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 5px;
  box-shadow: 0 0 0.5rem 0.5rem rgba(0, 0, 0, 0.1);
  overflow: hidden;
  div[data-role="map"] {
    padding: 10px;
  }
  > header {
    padding: 10px 0px 0;
    display: flex;
    align-items: center;
    button {
      cursor: pointer;
      padding: 0 10px;
      > p {
        text-transform: capitalize;
        font-size: var(--step--1);
        color: var(--queen-blue);
      }
      :first-of-type {
        border-right: 1px solid rgba(var(--queen-blue-rgb), 0.3);
      }
    }
  }
`

const Footer = styled(FormFooter)`
  width: 100%;
`
const MAP_TYPES = [`nearmap`, `google`] as const
type MapType = (typeof MAP_TYPES)[number]

export const ResetLocationModal: FC = () => {
  const { t } = useTranslation()
  const { reset, save } = useSiteActions()
  const { isLoading } = useSiteStatus()
  const editing = useRecoilValue(editingState)
  const retainZoom = useRecoilValue(shouldRetainZoomState)
  const setRelocationModalIsOpen = useSetRecoilState(
    resetLocationModalIsOpenState,
  )
  const { save: saveDesign } = useDesignActions()
  const { save: saveProposal } = useProposalActions()

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await save({ repopulateImagery: true })
    await saveDesign()
    await saveProposal()
    setRelocationModalIsOpen(false)
    window.location.reload()
  }

  const onCancel = () => {
    reset()
    setRelocationModalIsOpen(false)
  }

  const preferredImage = useRecoilValue(preferredImageState)
  const source = preferredImage.source === `google` ? `google` : `nearmap`
  const [mapType, setMapType] = React.useState<MapType>(source)

  const isEditing = editing || (mapType === `google` && retainZoom)

  return (
    <form onSubmit={onSubmit}>
      <Wrapper>
        <header>
          {MAP_TYPES.map((m) => (
            <button key={m} onClick={(e) => (e.preventDefault(), setMapType(m))}>
              <p style={{ opacity: mapType === m ? 1 : 0.65, fontWeight: 500 }}>
                {m}
              </p>
            </button>
          ))}
        </header>
        {mapType === `nearmap` && (
          <div data-role="map">
            <GeocodeMap />
          </div>
        )}
        {mapType === `google` && <GoogleMap />}
        <Footer>
          <Button
            variant="default"
            onClick={onCancel}
            disabled={isLoading || !isEditing}
          >
            {t(`cancel`)}
          </Button>
          <Button type="submit" disabled={isLoading || !isEditing}>
            {isEditing ? t(`save`) : isLoading ? t(`saving`) : t(`saved`)}
          </Button>
        </Footer>
      </Wrapper>
    </form>
  )
}
